import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BadgeCheck, Building2, Search } from 'lucide-react';
import { PageHeader } from '../components/page/PageHeader';
import { CtaBand } from '../components/page/CtaBand';
import { Reveal } from '../components/ui/Reveal';
import { Badge } from '../components/ui/Badge';
import { CASE_STUDIES } from '../components/caseStudiesData';
const SEO = {
  title: 'Case Studies | Brands & Marketing Agencies Working With Talent Agencies | Zano',
  description: "See how brands and marketing agencies used Zano's live talent agency roster database to connect with represented creators, launch high-value campaigns, and receive faster responses from talent agencies.",
  keywords: 'influencer marketing case studies, talent agency campaigns, represented creators, influencer sourcing, talent agency partnerships, creator campaigns',
  ogTitle: 'Zano Success Stories | Brands & Marketing Agencies Working With Talent Agencies',
  ogDescription: 'See how brands and agencies used Zano to work directly with talent agencies.'
};
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector<HTMLMetaElement>(selector);
  const tag = existingTag ?? document.createElement('meta');
  const previousContent = existingTag?.getAttribute('content') ?? null;
  if (!existingTag) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return () => {
    if (!existingTag) {
      tag.remove();
      return;
    }
    if (previousContent === null) {
      existingTag.removeAttribute('content');
    } else {
      existingTag.setAttribute('content', previousContent);
    }
  };
}
export const CaseStudies = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;
    const restoreDescription = updateMetaTag('name', 'description', SEO.description);
    const restoreKeywords = updateMetaTag('name', 'keywords', SEO.keywords);
    const restoreOgTitle = updateMetaTag('property', 'og:title', SEO.ogTitle);
    const restoreOgDescription = updateMetaTag('property', 'og:description', SEO.ogDescription);
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreKeywords();
      restoreOgTitle();
      restoreOgDescription();
    };
  }, []);
  return <>
      <PageHeader eyebrow="Case Studies" title="Teams moving from discovery to activation" subtitle="How brands and marketing agencies use Zano to source and manage talent agency relationships at scale." data-id="element-2198" data-mp="qgq97tyk01qa" />

      <section aria-labelledby="positioning-banner-title" className="mx-auto max-w-[1584px] px-4 pb-10 sm:px-6 lg:px-8" data-id="element-2199" data-mp="zgdbehz3xbke">
        <Reveal data-id="element-2200" data-mp="2x9996nhnm8z1">
          <div className="relative overflow-hidden rounded-3xl bg-zano-charcoal px-6 py-8 sm:px-10 sm:py-10 xl:aspect-[4/1] xl:px-14 xl:py-7" data-id="element-2201" data-mp="318rtbh1nig0t">
            <div className="pointer-events-none absolute right-0 top-0 h-32 w-32 -translate-y-1/2 translate-x-1/3 rounded-full border-[22px] border-zano-pink/20" data-id="element-2202" data-mp="2tq0mt3abbtsn" />
            <div className="pointer-events-none absolute bottom-0 left-0 h-44 w-44 -translate-x-1/2 translate-y-1/2 rounded-full border-[18px] border-white/5" data-id="element-2203" data-mp="2jz5x1oxuyc2g" />
            <div className="pointer-events-none absolute bottom-0 left-[30%] hidden h-[58%] w-px bg-white/10 xl:block" data-id="element-2204" data-mp="2fzopmpyvf4un" />

            <div className="relative grid h-full items-center gap-7 xl:grid-cols-[480px_minmax(0,1fr)_minmax(390px,0.82fr)] xl:gap-7" data-id="element-2205" data-mp="37p0bgaxts8ot">
              <div className="hidden self-stretch xl:block" aria-hidden="true" data-id="element-2206" data-mp="33mn2zerp1woc" />

              <div className="xl:pl-1" data-id="element-2207" data-mp="2yntp3l8hw3nn">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-zano-pink" data-id="element-2208" data-mp="1s89imjw5fzd2">
                  Your agency partner network
                </p>
                <h2 id="positioning-banner-title" className="max-w-[28rem] text-3xl font-bold leading-[1.08] text-white sm:text-4xl" data-id="element-2209" data-mp="1nb9ewfmrv8p4">
                  Keep partner rosters current.{' '}
                  <span className="text-zano-pink" data-id="element-2210" data-mp="325pv234o2v2m">
                    Discover new agencies at scale.
                  </span>
                </h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-gray-300 sm:text-base" data-id="element-2211" data-mp="3671bpj5kc5yo">
                  Stay up to date with the live rosters of agencies you already
                  work with, then discover vetted new talent agency partners in
                  the same place.
                </p>
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-300" data-id="element-2212" data-mp="3kxs4atezb92u">
                  <span className="inline-flex items-center gap-1.5" data-id="element-2213" data-mp="3pwvbokiflrdz">
                    <BadgeCheck size={15} className="text-zano-pink" data-id="element-2214" data-mp="29fppe22cddyp" />
                    Live partner rosters
                  </span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-2215" data-mp="2edivyw7k49zi">
                    <Building2 size={15} className="text-zano-pink" data-id="element-2216" data-mp="25x0at5qxs6sm" />
                    400+ vetted agencies
                  </span>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white p-3 text-left shadow-2xl" aria-label="Example of a live agency roster search" data-id="element-2217" data-mp="2fx0vav0jksye">
                <div className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-2" data-id="element-2218" data-mp="1n954g6xnlv9j">
                  <div className="flex gap-1" data-id="element-2219" data-mp="1i9s7gtd2j7t8">
                    <span className="h-2 w-2 rounded-full bg-rose-300" data-id="element-2220" data-mp="1i6xnf2xyvp0c" />
                    <span className="h-2 w-2 rounded-full bg-amber-300" data-id="element-2221" data-mp="1wyokz5xw9d18" />
                    <span className="h-2 w-2 rounded-full bg-emerald-300" data-id="element-2222" data-mp="1s6wu0uatt5ak" />
                  </div>
                  <span className="ml-1 text-[11px] font-semibold text-gray-500" data-id="element-2223" data-mp="14gu70slrqpit">
                    Live Roster Database
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold text-emerald-700" data-id="element-2224" data-mp="zgev85h4xq2y">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" data-id="element-2225" data-mp="34itoom06s8sw" />
                    Updated live
                  </span>
                </div>

                <div className="flex items-center gap-2 rounded-lg border border-zano-pink/30 bg-zano-blush/50 px-3 py-1.5" data-id="element-2226" data-mp="3dc5odcsuh3be">
                  <Search size={15} className="shrink-0 text-zano-pink" data-id="element-2227" data-mp="2gmfzko75bfq2" />
                  <span className="truncate text-xs font-medium text-gray-700" data-id="element-2228" data-mp="2lkzkcla06lgu">
                    Search represented creators
                  </span>
                  <span className="ml-auto hidden rounded-md bg-white px-2 py-1 text-[10px] font-medium text-gray-500 sm:inline" data-id="element-2229" data-mp="2plt9tj33g8bu">
                    Filters
                  </span>
                </div>

                <div className="mt-2 space-y-1.5" data-id="element-2230" data-mp="4vu29apyo19r">
                  {[{
                  name: 'Clicks Talent',
                  logo: 'https://cdn.magicpatterns.com/uploads/oKbracun8P5MXQXuDPFXrA/clicks_talent_logo.webp'
                }, {
                  name: 'United Talent Agency',
                  logo: 'https://cdn.magicpatterns.com/uploads/iMEkKfrRdejvDfRHdG5tcE/uta.png'
                }, {
                  name: 'Famfluence',
                  logo: 'https://cdn.magicpatterns.com/uploads/iq5ZMVZ2aiG8SYPyU7XxgR/famfluence_logo.png'
                }].map(agency => <div key={agency.name} className="flex min-w-0 items-center gap-3 rounded-xl border border-gray-100 px-3 py-1.5" data-id="element-2231" data-mp="3sdvrpga5km0e">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white p-1" data-id="element-2232" data-mp="3jgldpn5yairs">
                        <img src={agency.logo} alt={`${agency.name} logo`} className="h-full w-full object-contain" data-id="element-2233" data-mp="1hnf4hpgue2qw" />
                      </div>
                      <span className="truncate text-xs font-semibold text-gray-800" data-id="element-2234" data-mp="24wz6zigbef1m">
                        {agency.name}
                      </span>
                      <BadgeCheck size={14} className="ml-auto shrink-0 text-emerald-500" data-id="element-2235" data-mp="1w4nfdt9gslk2" />
                    </div>)}
                </div>

                <p className="mt-2 text-center text-[10px] leading-relaxed text-gray-500" data-id="element-2236" data-mp="1m7sitfwr4v8f">
                  Agency-owned data, not scraped creator profiles.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16" data-id="element-2237" data-mp="32kzgo37l2jjv">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2238" data-mp="37k2ccx1obdf6">
          {CASE_STUDIES.map((c, i) => <Reveal key={c.slug} delay={i % 2 * 0.1} data-id="element-2239" data-mp="3hnr5z67g495d" data-mpi={i}>
              <Link to={`/case-studies/${c.slug}`} className="group flex flex-col h-full bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-float hover:-translate-y-1 transition-all duration-300" data-id="element-2240" data-mp="3hoitdtqw30kk">
                <div className={`h-40 w-full flex items-center justify-center p-8 ${c.logoBg}`} data-id="element-2241" data-mp="3lp2oysvtdw4l">
                  <img src={c.logo} alt={`${c.brand} logo`} className="max-h-full max-w-[180px] object-contain transition-transform duration-500 group-hover:scale-105" data-id="element-2242" data-mp="4b7a6694w63y" />
                </div>
                <div className="p-8 flex flex-col flex-1" data-id="element-2243" data-mp="98z0wwy1lm6k">
                  <div className="flex flex-wrap gap-2 mb-4" data-id="element-2244" data-mp="2f0w08ht78jl2">
                    {c.tags.map(tag => <Badge key={tag} className="bg-blue-50 border border-blue-200 text-blue-700" data-id="element-2245" data-mp="2b0lvt9l2218b">
                        {tag}
                      </Badge>)}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug group-hover:text-zano-pink transition-colors" data-id="element-2246" data-mp="33axd2vj2h3t6">
                    {c.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-1" data-id="element-2247" data-mp="2ye6vu4vj0266">
                    {c.subtitle}
                  </p>
                  <div className="flex items-center text-sm font-bold text-zano-pink mt-auto" data-id="element-2248" data-mp="3r09h7gzjelom">
                    Read Case Study
                    <ArrowUpRight size={16} className="ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" data-id="element-2249" data-mp="3m88b6csypr71" />
                  </div>
                </div>
              </Link>
            </Reveal>)}
        </div>
      </section>

      <CtaBand title="Be the next team to ditch the spreadsheets" subtitle="Start a free trial today and access the elite layer of talent representation." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} data-id="element-2250" data-mp="ds6d3srkcm8g" />
    </>;
};